import { useState } from "react";
import {
  useDownloadStockImportTemplate,
  useImportStock,
} from "../../hooks/useSowingInstructions";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage } from "@/shared/utils/errorHandler";
import { useConfirm } from "@/shared/contexts/confirm-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Download, AlertCircle, CheckCircle2, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { SowingInstructionImportResult } from "../../services/sowingInstructionsApi";

/**
 * Bulk-import dialog for greenhouse stock at go-live. Each row in the uploaded
 * Excel file is one SowingInstruction at its current production stage; rows at
 * Growing or Mature also create a GreenhouseStockEntry on the backend.
 *
 * Backend: POST /api/SowingInstructions/import (template download at GET .../template)
 * Mirrors the customer BulkImportDialog pattern.
 */
export const BulkImportStockDialog = () => {
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<SowingInstructionImportResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const importMutation = useImportStock();
  const downloadTemplateMutation = useDownloadStockImportTemplate();
  const notify = useNotification();
  const confirm = useConfirm();

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadTemplateMutation.mutateAsync();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "greenhouse_stock_import_template.xlsx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      notify.success("Template downloaded successfully");
    } catch {
      notify.error("Failed to download template");
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!validTypes.includes(file.type)) {
      notify.error("Please select a valid Excel file (.xlsx)");
      return;
    }
    setSelectedFile(file);
    setImportResult(null);
    setErrorMessage(null);
  };

  const handleUpload = () => {
    if (!selectedFile) {
      notify.error("Please select a file to upload");
      return;
    }
    confirm({
      title: "Import stock file?",
      description: `“${selectedFile.name}” will be imported. Valid rows create sowing instructions and stock entries.`,
      confirmLabel: "Import",
      onConfirm: () => performUpload(),
    });
  };

  const performUpload = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    try {
      const result = await importMutation.mutateAsync(selectedFile);
      setImportResult(result);
      setErrorMessage(null);

      if (result.failureCount === 0) {
        notify.success(
          `Imported ${result.successCount} sowing instruction(s); ${result.stockEntriesCreated} stock entry/entries created.`
        );
        setTimeout(() => handleClose(), 1500);
      } else {
        notify.warning(
          `Import completed with ${result.failureCount} error(s). Check details below.`
        );
      }
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      const e = error as { response?: { data?: { data?: SowingInstructionImportResult; message?: string } } };
      if (e?.response?.data?.data) {
        setImportResult(e.response.data.data);
        setErrorMessage(e.response.data.message ?? null);
        notify.error(e.response.data.message ?? message ?? "Import validation failed");
      } else {
        setErrorMessage(null);
        notify.error(message ?? "Failed to import stock");
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setImportResult(null);
    setErrorMessage(null);
  };

  const handleClose = () => {
    handleReset();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Upload className="w-4 h-4" />
          Bulk Import Stock
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Import Greenhouse Stock</DialogTitle>
          <DialogDescription>
            Load sowing instructions at any production stage. Rows at Growing or
            Mature also create greenhouse stock entries automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Step 1 — Template */}
          <div className="border-b pb-6">
            <h3 className="font-semibold mb-3">Step 1: Download Template</h3>
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              disabled={downloadTemplateMutation.isPending}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              {downloadTemplateMutation.isPending ? "Downloading..." : "Download Template"}
            </Button>
            <p className="text-sm text-gray-500 mt-2">
              The template includes one example row per stage (Sowing → Mature) and
              a help sheet explaining every column and validation rule.
            </p>
          </div>

          {/* Step 2 — File */}
          <div className="border-b pb-6">
            <h3 className="font-semibold mb-3">Step 2: Select & Upload File</h3>
            <div className="flex flex-col gap-4">
              <input
                type="file"
                accept=".xlsx"
                onChange={handleFileSelect}
                disabled={isUploading}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100
                  cursor-pointer"
              />
              {selectedFile && (
                <p className="text-sm text-gray-600">
                  Selected: <span className="font-medium">{selectedFile.name}</span>
                </p>
              )}
            </div>
          </div>

          {!importResult && (
            <div className="flex gap-3">
              <Button onClick={handleUpload} disabled={!selectedFile || isUploading}>
                {isUploading ? "Uploading..." : "Upload File"}
              </Button>
              {selectedFile && (
                <Button variant="outline" onClick={handleReset}>
                  Clear Selection
                </Button>
              )}
            </div>
          )}

          {importResult && (
            <div className="space-y-4 border-t pt-6">
              <h3 className="font-semibold">Import Results</h3>

              {errorMessage && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    {errorMessage}
                  </AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {importResult.totalRows}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Imported</p>
                  <p className="text-2xl font-bold text-green-600">
                    {importResult.successCount}
                  </p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Failed</p>
                  <p className="text-2xl font-bold text-red-600">
                    {importResult.failureCount}
                  </p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Stock entries</p>
                  <p className="text-2xl font-bold text-amber-700">
                    {importResult.stockEntriesCreated}
                  </p>
                </div>
              </div>

              {importResult.successCount > 0 && (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    {importResult.successCount} sowing instruction
                    {importResult.successCount !== 1 ? "s" : ""} imported. Mature/Growing rows
                    produced {importResult.stockEntriesCreated} stock entry/entries.
                  </AlertDescription>
                </Alert>
              )}

              {importResult.errors.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    Import Errors
                  </h4>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-64 overflow-y-auto space-y-3">
                    {importResult.errors.map((error, index) => (
                      <div key={index} className="text-sm">
                        <div className="font-medium text-red-900">
                          Row {error.rowNumber}:{" "}
                          {error.instructionNumber || "(no instruction number)"}
                          {error.stage ? ` — ${error.stage}` : ""}
                        </div>
                        <ul className="list-disc list-inside text-red-700 ml-2 text-xs">
                          {error.errors.map((err, errIndex) => (
                            <li key={errIndex}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button onClick={handleReset} variant="outline">
                  Import Another File
                </Button>
                <Button onClick={handleClose}>Close</Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
